<template>
  <el-dialog
    :title="$t('플랫폼 메뉴 연결')"
    :visible="visible"
    width="720px"
    :close-on-click-modal="false"
    @close="close">
    <div v-if="link">
      <el-descriptions :column="2" border size="small">
        <el-descriptions-item :label="$t('플랫폼')">
          {{ platformName(link.platform) }}
        </el-descriptions-item>
        <!-- `이름(번호)` 한 줄 — 조립 규칙은 utils/deliveryMenuSync 한 곳 (§16.179) -->
        <el-descriptions-item :label="$t('가게')">{{ shop(link) }}</el-descriptions-item>
        <el-descriptions-item :label="$t('플랫폼 메뉴명')">
          <span v-if="link.observedName">{{ link.observedName }}</span>
          <span v-else class="dm-bind__key">
            {{ link.menuNameKey }}
            <el-tag size="mini" type="info">{{ $t('정규화 키 (원문 아님)') }}</el-tag>
          </span>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('플랫폼 가격')">{{ priceText }}</el-descriptions-item>
        <el-descriptions-item :label="$t('메뉴판')" v-if="link.menuBoardRef">
          {{ link.menuBoardRef }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('관측 시각')">{{ formatDate(link.observedAt) }}</el-descriptions-item>
      </el-descriptions>

      <div class="dm-bind__section">
        <div class="dm-bind__label">{{ $t('연결할 오더엔 메뉴') }}</div>

        <el-radio-group v-model="smenuCd" v-if="candidates.length > 0" class="dm-bind__candidates">
          <el-radio v-for="c in candidates" :key="c.smenuCd" :label="c.smenuCd" border size="small">
            {{ c.smenuNm }}
          </el-radio>
        </el-radio-group>
        <el-alert
          v-else
          :title="$t('이름이 비슷한 후보를 찾지 못했습니다. 아래에서 직접 검색해 선택해 주세요.')"
          type="info"
          :closable="false"
          show-icon></el-alert>

        <div class="dm-bind__search">
          <el-input
            v-model="keyword"
            :placeholder="$t('메뉴명으로 검색')"
            size="small"
            clearable
            @keyup.enter.native="searchMenus">
            <el-button slot="append" icon="el-icon-search" @click="searchMenus"></el-button>
          </el-input>
          <el-table
            v-if="searched.length > 0"
            :data="searched"
            size="mini"
            border
            height="200"
            highlight-current-row
            @current-change="selectSearched"
            class="dm-bind__result">
            <el-table-column :label="$t('메뉴명')" prop="smenuNmLan" show-overflow-tooltip></el-table-column>
            <el-table-column :label="$t('가격')" prop="price" width="110" align="right"></el-table-column>
            <el-table-column :label="$t('사용')" prop="useYn" width="70" align="center"></el-table-column>
          </el-table>
        </div>

        <div class="dm-bind__chosen" v-if="smenuCd">
          {{ $t('선택') }}: <strong>{{ chosenName }}</strong> (smenuCd {{ smenuCd }})
        </div>
      </div>
    </div>

    <div slot="footer">
      <el-button size="small" @click="close">{{ $t('취소') }}</el-button>
      <el-button size="small" type="primary" :disabled="!smenuCd" :loading="saving" @click="bind">
        {{ $t('연결 확정') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { util } from '@/mixins/utilMixin'
import {
  DELIVERY_MENU_API,
  deliveryMenuErrorMessage,
  deliveryMenuPlatformName,
  deliveryMenuTimeText,
  observedPriceText,
  shopText,
} from '@/utils/deliveryMenuSync'
import { ALL_ROWS_PAGE_SIZE, pageRows } from '@/utils/pagedList'

/**
 * 플랫폼 메뉴 ↔ 오더엔 메뉴 확정 (`POST /links/{id}/bind`).
 *
 * **③ 「메뉴 연결」 화면과 ① 메뉴 목록이 함께 쓴다.** `bind` 는 링크 상태를 가리지 않고 `LINKED`
 * 로 확정하므로 두 화면이 같은 확정 UI 를 쓴다 — ③ 전용으로 만들지 않는다.
 *
 * `AMBIGUOUS` 링크는 **양쪽 모두**에서 확정할 수 있다 (§16.257). ①은 그 메뉴를 이미 보고 있을
 * 때의 자리이고, ③은 **어느 메뉴가 걸렸는지 모를 때** 찾는 자리다 — 후자가 없어 사장님이
 * 「연결 상태를 확인해 주세요」를 듣고도 그 메뉴를 못 찾던 것이 이 회차가 고친 것이다.
 */
export default {
  name: 'DeliveryMenuBindDialog',
  mixins: [mainVueMixin],

  props: {
    visible: { type: Boolean, default: false },
    /** `DeliveryMenuOrphanView` 또는 ①에서 조립한 `{linkId, platform, shopNo, candidates, …}` */
    link: { type: Object, default: null },
    /** 링크가 속한 매장 — 메뉴 검색 스코프. 그룹 형제 매장일 수 있다 (§9.4) */
    storeCd: { type: Number, default: null },
  },

  data() {
    return {
      smenuCd: null,
      keyword: '',
      searched: [],
      searchedName: '',
      saving: false,
    }
  },

  computed: {
    candidates() {
      return (this.link && this.link.candidates) || []
    },
    priceText() {
      // 관측 원문은 문자열이며 숫자가 아닐 수 있다 — 표기 규칙은 utils/deliveryMenuSync 가 소유한다
      return observedPriceText(this.link && this.link.observedPrice)
    },
    chosenName() {
      const hit = this.candidates.find((c) => c.smenuCd === this.smenuCd)
      if (hit) return hit.smenuNm
      return this.searchedName || '-'
    },
  },

  watch: {
    visible(open) {
      if (!open) return
      this.smenuCd = this.candidates.length === 1 ? this.candidates[0].smenuCd : null
      this.keyword = (this.link && (this.link.observedName || this.link.menuNameKey)) || ''
      this.searched = []
      this.searchedName = ''
    },
  },

  methods: {
    platformName: deliveryMenuPlatformName,
    /** 샵 표기 규칙은 utils/deliveryMenuSync 한 곳이 소유한다 (§16.179) */
    shop: shopText,

    formatDate(value) {
      return deliveryMenuTimeText(value, util.getDate)
    },

    async searchMenus() {
      // 이 창은 키워드로 좁혀 고르는 자리라 페이지를 넘기지 않는다 — 좁힌 결과의 **전량**을 받는다
      // ([ALL_ROWS_PAGE_SIZE]). 상한을 추측해 적으면 그 수를 넘긴 매장에서 찾던 메뉴가 조용히 빠진다.
      const params = { smenuNm: this.keyword || '', useYn: 'Y', pageSize: ALL_ROWS_PAGE_SIZE }
      // 링크의 매장이 세션 매장과 다를 수 있다(형제 그룹). 요청마다 대상 매장을 명시한다 (§9.4)
      if (this.storeCd != null) params.storeCd = this.storeCd
      try {
        const res = await this.$axios.get('/smenu/store', { params })
        // 봉투든 배열이든 행을 꺼내는 일은 [pageRows] 한 곳이 한다 — `Array.isArray` 로 갈래를 나누면
        // 봉투가 온 날 검색 결과가 **조용히 0건**이 된다
        this.searched = pageRows(res && res.data)
      } catch (e) {
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e) })
      }
    },

    selectSearched(row) {
      if (!row) return
      this.smenuCd = row.smenuCd
      this.searchedName = row.smenuNmLan
    },

    async bind() {
      if (!this.smenuCd || !this.link) return
      this.saving = true
      try {
        await this.$axios.post(`${DELIVERY_MENU_API}/links/${this.link.linkId}/bind`, { smenuCd: this.smenuCd })
        this.$message({ type: 'success', message: this.$t('연결했습니다.') })
        this.$emit('bound', { linkId: this.link.linkId, smenuCd: this.smenuCd })
        this.close()
      } catch (e) {
        // 400 은 서버가 "먼저 그 연결을 정리한 뒤" 같은 다음 행동을 담아 내린다 — 그대로 노출한다
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e), duration: 6000 })
      } finally {
        this.saving = false
      }
    },

    close() {
      this.$emit('update:visible', false)
    },
  },
}
</script>

<style scoped>
.dm-bind__section {
  margin-top: 16px;
}
.dm-bind__label {
  font-weight: 600;
  margin-bottom: 8px;
}
.dm-bind__candidates >>> .el-radio {
  margin-bottom: 6px;
}
.dm-bind__search {
  margin-top: 12px;
}
.dm-bind__result {
  margin-top: 8px;
}
.dm-bind__chosen {
  margin-top: 10px;
  color: #409eff;
}
.dm-bind__key {
  color: #909399;
}
</style>
